import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';

// Platform Icons
const YoutubeIcon = () => (
  <img src="/images/categories/youtube.svg" alt="YouTube" width="80" height="80" className="w-16 h-16 sm:w-20 sm:h-20" loading="lazy" decoding="async" />
);

const InstagramIcon = () => (
  <img src="/images/categories/instagram.svg" alt="Instagram" width="80" height="80" className="w-16 h-16 sm:w-20 sm:h-20" loading="lazy" decoding="async" />
);

const TiktokIcon = () => (
  <img src="/images/categories/tiktok.svg" alt="TikTok" width="80" height="80" className="w-16 h-16 sm:w-20 sm:h-20" loading="lazy" decoding="async" />
);

const FacebookIcon = () => (
  <img src="/images/categories/facebook.svg" alt="Facebook" width="80" height="80" className="w-16 h-16 sm:w-20 sm:h-20" loading="lazy" decoding="async" />
);

// Badge icons
const VerifiedIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
);
const MonetizedIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13v2.062A3.001 3.001 0 0113 10c0 1.306-.835 2.417-2 2.83V15a1 1 0 11-2 0v-2.062A3.001 3.001 0 017 10c0-1.306.835-2.417 2-2.83V5a1 1 0 112 0zm-1 4a1 1 0 100 2 1 1 0 000-2z"/></svg>
);
const EmailIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
);

const FollowersIcon = () => (
  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1" fill="#6B7280" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05C16.16 13.36 18 14.28 18 15.5V19h6v-2.5c0-2.33-4.67-3.5-8-3.5z"/></svg>
);

// Helper to slugify product title
function slugify(title) {
  if (!title || typeof title !== 'string') return '';
  
  // Sanitize input to prevent XSS
  const safeTitle = title.replace(/[<>\"'&]/g, '');
  
  return safeTitle
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

const ProductCard = ({ product, width = 360, height = 560, showEmail = true, onDetails, featured = false, priority = false, showBoostedBadge = true, showBoostedBar = true }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [showPassPopup, setShowPassPopup] = useState(false);
  if (!product) return null;
  

  

  // Centralized logic for all fields
  const image = product.mainImage ? getImageUrl(product.mainImage) : product.image;
  const title = product.title;
  const category = product.stats?.category || product.category;
  const followers = product.stats?.followers || product.followers;
  const monetizedRaw = (product.stats && product.stats.monetized !== undefined)
    ? product.stats.monetized
    : product.monetized;
  const monetized = (() => {
    if (typeof monetizedRaw === 'string') {
      const v = monetizedRaw.trim().toLowerCase();
      return v === 'yes' || v === 'true' || v === 'y';
    }
    return Boolean(monetizedRaw);
  })();
  const verified = product.verified;
  const price = product.salePrice || product.price;
  const oldPrice = product.salePrice ? product.price : undefined;
  const onSale = !!product.salePrice;
  const accountType = product.accountType || product.platform || '';

  function getPlatformIcon(type) {
    // Sanitize input to prevent XSS
    if (!type || typeof type !== 'string') return null;
    
    const safeType = type.toLowerCase().replace(/[^a-z]/g, '');
    
    switch (safeType) {
      case 'youtube':
        return <YoutubeIcon />;
      case 'instagram':
        return <InstagramIcon />;
      case 'tiktok':
        return <TiktokIcon />;
      case 'facebook':
        return <FacebookIcon />;
      default:
        return null;
    }
  }

  return (
    <>
      <a
        itemScope
        itemType="https://schema.org/Product"
        href={(() => {
          const isValidSlug = product.slug && /^[a-z0-9-]+$/.test(product.slug);
          return isValidSlug ? `/product/${product.slug}` : `/product/${product._id}`;
        })()}
        className="w-full h-full bg-white dark:bg-[#282828] rounded-lg sm:rounded-xl lg:rounded-2xl shadow-sm flex flex-col overflow-hidden border border-gray-100 dark:border-gray-700 transition-all duration-300 mx-auto hover:shadow-xl hover:scale-[1.02] hover:z-10 hover:border-red-500/40 dark:hover:border-red-500/40 dark:hover:shadow-2xl dark:hover:shadow-red-500/10 cursor-pointer"
        style={{ textDecoration: 'none' }}
      >
      <meta itemProp="name" content={(() => {
        const safeTitle = (title || '').replace(/[<>\"'&]/g, '');
        return safeTitle || 'Product Title';
      })()} />
      <meta itemProp="image" content={(() => {
        const safeImage = (image || '').replace(/[<>\"'&]/g, '');
        return safeImage || '/images/placeholder-product.png';
      })()} />
      <meta itemProp="price" content={(() => {
        const safePrice = (price || '').toString().replace(/[<>\"'&]/g, '');
        return safePrice || '0';
      })()} />
      <meta itemProp="priceCurrency" content="USD" />
      <meta itemProp="availability" content={product.stockStatus === 'outofstock' ? 'OutOfStock' : 'InStock'} />
      {/* Blue highlight bar for boosted (can be hidden via prop) */}
      {product.boosted && showBoostedBar && (
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-t-lg sm:rounded-t-xl lg:rounded-t-2xl animate-pulse"></div>
      )}
      {/* Image */}
      <div className="w-full aspect-[4/3] bg-gray-100 overflow-hidden relative">
        {/* Lazy loading placeholder */}
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse" />
        
        <img
          itemProp="image"
          src={(() => {
            const safeImage = (image || '').replace(/[<>\"'&]/g, '');
            return safeImage || '/images/placeholder-product.png';
          })()}
          srcSet={(() => {
            const safeImage = (image || '').replace(/[<>\"'&]/g, '');
            return safeImage ? `${safeImage}?w=360 360w, ${safeImage}?w=720 720w, ${safeImage}?w=1080 1080w` : '';
          })()}
          sizes="(max-width: 640px) 50vw, (max-width: 1280px) 33vw, 360px"
          alt={(() => {
            const safeTitle = (title || '').replace(/[<>\"'&]/g, '');
            return safeTitle || 'Product Image';
          })()}
          width="360"
          height="270"
          className="w-full h-full object-cover transition-opacity duration-300"
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={priority ? 'high' : 'auto'}
          onLoad={(e) => {
            e.target.style.opacity = '1';
            if (e.target.previousElementSibling) {
              e.target.previousElementSibling.style.display = 'none';
            }
          }}
          onError={(e) => {
            // Fallback to a local placeholder if loading fails
            e.currentTarget.src = '/images/placeholder-product.png';
            e.currentTarget.srcset = '';
            e.currentTarget.style.opacity = '1';
            if (e.currentTarget.previousElementSibling) {
              e.currentTarget.previousElementSibling.style.display = 'none';
            }
          }}
          style={{ opacity: 0 }}
        />
        
        {/* Boosted icon - rendered AFTER image to ensure visibility */}
        {product.boosted && showBoostedBadge && (
          <div className="absolute top-1.5 left-1.5 sm:top-2 lg:top-0 sm:left-2 lg:left-0 z-10">
            <span className="inline-flex items-center justify-center bg-blue-700 dark:bg-blue-600 text-white font-bold px-2 sm:px-3 lg:px-4 py-1 sm:py-1.5 lg:py-2 rounded-md sm:rounded-lg lg:rounded-xl shadow-lg dark:shadow-blue-500/30 border border-blue-700 dark:border-blue-600">
              {/* Large icon only on mobile */}
              <svg className="w-6 h-6 sm:hidden" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8L21 10h-9l1-8z" /></svg>
              {/* Icon + text on sm and up */}
              <span className="hidden sm:inline-flex items-center">
                <svg className="w-4 h-4 lg:w-5 lg:h-5 mr-1 -ml-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8L21 10h-9l1-8z" /></svg>
                <span className="hidden lg:inline">Boosted</span>
                <span className="lg:hidden">Boost</span>
              </span>
            </span>
          </div>
        )}
        {/* PASS badge - only show for verified sellers */}
        {product.userId?.verified && (
          <div className="absolute bottom-0.5 left-0.5 sm:bottom-1 lg:bottom-1.5 sm:left-1 lg:left-1.5 z-10">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPassPopup(true);
              }}
              className="inline-flex items-center justify-center text-white font-bold px-1 sm:px-1.5 lg:px-2 py-0.5 sm:py-0.5 lg:py-1 rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 cursor-pointer"
              style={{
                backgroundColor: '#024336',
                border: '1px solid #024336',
                boxShadow: '0 4px 6px -1px rgba(2, 67, 54, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#035a47';
                e.target.style.borderColor = '#035a47';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#024336';
                e.target.style.borderColor = '#024336';
              }}
            >
              <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 mr-0.5 sm:mr-1 lg:mr-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              <span className="text-[8px] sm:text-[10px] lg:text-xs font-bold">PASS</span>
            </button>
          </div>
        )}
        {featured && (
          <div className="absolute top-1.5 right-1.5 sm:top-2 lg:top-3 sm:right-2 lg:right-3 z-20">
            <span className="inline-flex items-center px-2 sm:px-2 lg:px-2 py-1 sm:py-1 lg:py-1 bg-red-600 text-white text-xs sm:text-xs lg:text-xs font-bold rounded-full shadow-lg border border-white">
              <svg className="w-3 h-3 sm:w-3 sm:h-3 lg:w-4 lg:h-4 mr-1 sm:mr-1 lg:mr-1" fill="currentColor" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 17.75l-6.172 3.245 1.179-6.873L2 9.505l6.914-1.004L12 2.75l3.086 5.751L22 9.505l-5.007 4.617 1.179 6.873z" /></svg>
              <span className="text-xs font-bold">Featured</span>
            </span>
          </div>
        )}
      </div>
      {/* Category Row */}
      <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 lg:px-5 mt-1.5 sm:mt-2 lg:mt-3 mb-2 sm:mb-3 lg:mb-4">
        {/* Category badge */}
        <div className="flex items-center p-0.5 sm:p-1 lg:p-1 pr-1.5 sm:pr-2 lg:pr-3 rounded-md sm:rounded-lg lg:rounded-[12px] border border-white/60 bg-white/60 backdrop-blur-md shadow-md dark:border-gray-600/60 dark:bg-gray-700/60">
          <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full bg-gradient-to-br from-white to-gray-200 border border-white/60 mr-1 sm:mr-1.5 lg:mr-2 dark:from-gray-600 dark:to-gray-500 dark:border-gray-500/60">
            <img
              src={`/images/categories/${(accountType || category || '').toLowerCase().replace(/[^a-z0-9]/g, '')}.svg`}
              alt={(() => {
                const safeCategory = (accountType || category || '').replace(/[<>\"'&]/g, '');
                return safeCategory || 'Category';
              })()}
              className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4"
              loading="lazy"
            />
          </div>
          <span className="text-xs sm:text-sm lg:text-base font-semibold text-black capitalize drop-shadow-sm dark:text-gray-100">
            {(() => {
              const safeCategory = (accountType || category || '').replace(/[<>\"'&]/g, '');
              return safeCategory.charAt(0).toUpperCase() + safeCategory.slice(1);
            })()}
          </span>
        </div>
        {/* Monetized badge */}
        {monetized && (
          <div className="flex items-center p-0.5 sm:p-1 lg:p-1 pr-1.5 sm:pr-2 lg:pr-3 rounded-md sm:rounded-lg lg:rounded-[12px] border border-white/60 bg-white/60 backdrop-blur-md shadow-md ml-1 sm:ml-2 dark:border-gray-600/60 dark:bg-gray-700/60">
            <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full bg-gradient-to-br from-white to-gray-200 border border-white/60 mr-1 sm:mr-1.5 lg:mr-2 dark:from-gray-600 dark:to-gray-500 dark:border-gray-500/60">
              <svg 
                className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-green-400" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
              </svg>
            </div>
            <span className="text-xs sm:text-sm lg:text-base font-semibold text-black drop-shadow-sm dark:text-gray-100">
              Monetized
            </span>
          </div>
        )}
      </div>
      {/* Title */}
      <div className="px-2 sm:px-3 lg:px-5">
        <h3
          itemProp="name"
          className="font-bold text-sm sm:text-lg lg:text-xl text-gray-900 dark:text-white leading-tight mb-1.5 sm:mb-2 lg:mb-2 cursor-pointer hover:text-red-600 dark:hover:text-red-400 transition-colors mt-0"
          title={(() => {
            const safeTitle = (title || '').replace(/[<>\"'&]/g, '');
            return safeTitle || 'Product Title';
          })()}
          style={{ textDecoration: 'none', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {(() => {
            const safeTitle = (title || '').replace(/[<>\"'&]/g, '');
            return safeTitle || 'Product Title';
          })()}
        </h3>
        {/* Followers */}
        {typeof followers !== 'undefined' && followers !== '' && (
          <>
            <div className="flex items-center gap-1 text-xs sm:text-sm lg:text-base font-semibold text-gray-900 dark:text-gray-200 mb-1.5 sm:mb-2 lg:mb-3 mt-1">
              <FollowersIcon />
              <span className="tabular-nums">{(() => {
                const safeFollowers = Number(followers || 0);
                return isNaN(safeFollowers) ? '0' : safeFollowers.toLocaleString();
              })()}</span>
            </div>
            <div className="border-b border-gray-200 dark:border-gray-600 mb-2 sm:mb-3 lg:mb-4" />
          </>
        )}
      </div>
      {/* Price Row */}
      <div className="flex items-center px-2 sm:px-3 lg:px-5 mb-2 sm:mb-3 lg:mb-4 mt-auto gap-1.5 sm:gap-2">
        <span className="text-sm sm:text-base lg:text-lg font-bold text-black dark:text-white">USD</span>
        <span className="text-sm sm:text-base lg:text-lg font-bold text-black dark:text-white">${(() => {
          const safePrice = (price || 0).toString().replace(/[<>\"'&]/g, '');
          return safePrice || '0';
        })()}</span>
        {product.stockStatus === 'outofstock' ? (
          <span className="ml-1.5 sm:ml-2 bg-gray-500 text-white text-[10px] sm:text-sm lg:text-base px-1 sm:px-2 lg:px-3 py-0 sm:py-1 rounded-sm sm:rounded-full font-bold">out of stock</span>
        ) : (
          <>
            {oldPrice && (
              <span className="text-gray-600 dark:text-gray-300 line-through text-xs sm:text-sm lg:text-base ml-1">${(() => {
                const safeOldPrice = (oldPrice || 0).toString().replace(/[<>\"'&]/g, '');
                return safeOldPrice || '0';
              })()}</span>
            )}
            {onSale && (
              <span className="ml-1.5 sm:ml-2 bg-red-600 text-white text-xs px-1.5 sm:px-2 lg:px-3 py-0.5 sm:py-1 rounded-full font-bold">Sale!</span>
            )}
          </>
        )}
      </div>
      {/* Details Button */}
      <div className="px-2 sm:px-3 lg:px-5 pb-2 sm:pb-3 lg:pb-5 mt-1.5 sm:mt-2">
        <span
          className={`w-full border font-semibold py-2 sm:py-2.5 lg:py-2 rounded-md sm:rounded-lg lg:rounded-xl transition-all duration-300 text-xs sm:text-sm lg:text-base flex items-center justify-center ${product.stockStatus === 'outofstock' ? 'bg-gray-300 text-gray-700 border-gray-400 hover:bg-gray-400 hover:text-white' : 'border-gray-500 dark:border-gray-500 hover:bg-red-600 hover:text-white text-gray-900 dark:text-white dark:hover:bg-red-500 dark:hover:text-white dark:hover:border-red-500 hover:shadow-lg dark:hover:shadow-red-500/20'}`}
          style={{ minHeight: 44 }}
        >
          View Details
        </span>
      </div>
    </a>

    {/* PASS Verification Popup */}
    {showPassPopup && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className={`relative w-full max-w-md sm:max-w-lg lg:max-w-xl mx-auto rounded-2xl shadow-2xl transition-all duration-300 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 border border-gray-700/50' 
            : 'bg-white border border-gray-200'
        }`}>
          {/* Header with close button */}
          <div className="flex items-center justify-between p-4 sm:p-5 lg:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <div>
                <h3 className={`text-lg sm:text-xl font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Verified Seller</h3>
                <p className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>Identity Confirmed</p>
              </div>
            </div>
            <button
              onClick={() => setShowPassPopup(false)}
              className={`p-2 rounded-full transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-5 lg:p-6">
            <div className={`text-sm sm:text-base leading-relaxed transition-colors duration-300 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>

              <div className={`p-3 sm:p-4 rounded-xl border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gray-800/50 border-gray-700/50' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                                 <div className="mb-2">
                   <span className={`text-sm font-semibold transition-colors duration-300 ${
                     isDarkMode ? 'text-green-400' : 'text-green-600'
                   }`}>Verification Complete</span>
                 </div>
                                 <p className={`text-xs sm:text-sm transition-colors duration-300 ${
                   isDarkMode ? 'text-gray-400' : 'text-gray-600'
                 }`}>
                   Seller has provided their ID details and a selfie video to help us confirm their identity. We may share this information with law enforcement if fraudulent activity occurs.
                 </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 sm:p-5 lg:p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowPassPopup(false)}
              className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 hover:border-gray-500'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 hover:border-gray-400'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
        </>
    );
  };

export default React.memo(ProductCard);